import { XCircle, Send, CheckCircle, AlertCircle } from "lucide-react";

const AdvancementModal = ({
  t,
  styles,
  theme,
  language,
  isRTL,
  advancementForm,
  setAdvancementForm,
  advancementLimits,
  advancementValidation,
  setShowAdvancementModal,
  handleAdvancementSubmit,
  formatCurrency,
  setAlertModal,
}) => {
  const focusStyle = (e) => {
    e.currentTarget.style.borderColor = "#3b82f6";
    e.currentTarget.style.boxShadow =
      theme === "dark"
        ? "0 0 0 4px rgba(59, 130, 246, 0.2), 0 8px 24px rgba(0, 0, 0, 0.3)"
        : "0 0 0 4px rgba(59, 130, 246, 0.15), 0 8px 24px rgba(0, 0, 0, 0.06)";
  };

  const blurStyle = (e) => {
    e.currentTarget.style.borderColor =
      theme === "dark" ? "rgba(255, 255, 255, 0.15)" : "rgba(0, 0, 0, 0.1)";
    e.currentTarget.style.boxShadow =
      theme === "dark" ? "0 6px 18px rgba(0, 0, 0, 0.3)" : "0 6px 18px rgba(0, 0, 0, 0.06)";
  };

  const handleClose = () => {
    setShowAdvancementModal(false);
    setAdvancementForm({
      requestedAmount: "",
      reason: "",
      urgency: "normal",
      numberOfInstallments: "",
      repaymentStartDate: "",
    });
  };

  return (
    <div style={styles.modalOverlay} onClick={handleClose}>
      <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>{t.requestSalaryAdvancement}</h3>
          <button
            style={styles.closeButton}
            onClick={handleClose}
            onMouseEnter={(e) => {
              e.currentTarget.style.background =
                theme === "dark" ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.06)";
            }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "none"; }}
          >
            <XCircle size={28} />
          </button>
        </div>

        {/* Amount */}
        <div style={styles.formGroup}>
          <label style={styles.formLabel}>
            {t.requestedAmount} <span style={{ color: "#ef4444" }}>{t.required}</span>
          </label>
          <input
            type="number"
            style={styles.formInput}
            value={advancementForm.requestedAmount}
            onChange={(e) => {
              const value = parseFloat(e.target.value) || 0;
              const maxAmount = advancementLimits?.maxAmount || Infinity;
              if (value > maxAmount) {
                setAdvancementForm({ ...advancementForm, requestedAmount: maxAmount.toString() });
                setAlertModal({
                  open: true,
                  title: language === "ar" ? "الحد الأقصى للسلفة" : "Maximum advancement",
                  message:
                    language === "ar"
                      ? `الحد الأقصى للسلفة هو ${formatCurrency(maxAmount)}`
                      : `Maximum advancement is ${formatCurrency(maxAmount)}`,
                  variant: "info",
                  showCancel: false,
                  onConfirm: () => setAlertModal((prev) => ({ ...prev, open: false })),
                });
              } else {
                setAdvancementForm({ ...advancementForm, requestedAmount: e.target.value });
              }
            }}
            placeholder={t.enterAmount}
            min="0"
            max={advancementLimits?.maxAmount || undefined}
            step="100"
            onFocus={focusStyle}
            onBlur={blurStyle}
          />
        </div>

        {/* Reason */}
        <div style={styles.formGroup}>
          <label style={styles.formLabel}>
            {t.reason} <span style={{ color: "#ef4444" }}>{t.required}</span>
          </label>
          <textarea
            style={styles.formTextarea}
            value={advancementForm.reason}
            onChange={(e) => setAdvancementForm({ ...advancementForm, reason: e.target.value })}
            placeholder={t.whyNeedAdvancement}
            onFocus={focusStyle}
            onBlur={blurStyle}
          />
        </div>

        {/* Installments */}
        <div style={styles.formGroup}>
          <label style={styles.formLabel}>
            {t.numberOfInstallments}{" "}
            <span style={{ color: "rgba(107, 114, 128, 0.8)" }}>{t.optional}</span>
          </label>
          <input
            type="number"
            style={styles.formInput}
            value={advancementForm.numberOfInstallments}
            onChange={(e) => {
              const value = parseInt(e.target.value) || 0;
              const minInst = advancementLimits?.installments?.min || 1;
              const maxInst = advancementLimits?.installments?.max || 12;
              if (value > maxInst) {
                setAdvancementForm({ ...advancementForm, numberOfInstallments: maxInst.toString() });
                setAlertModal({
                  open: true,
                  title: language === "ar" ? "الحد الأقصى للأقساط" : "Maximum installments",
                  message:
                    language === "ar"
                      ? `الحد الأقصى للأقساط هو ${maxInst} شهر`
                      : `Maximum installments is ${maxInst} months`,
                  variant: "info",
                  showCancel: false,
                  onConfirm: () => setAlertModal((prev) => ({ ...prev, open: false })),
                });
              } else if (value < minInst && value > 0) {
                setAdvancementForm({ ...advancementForm, numberOfInstallments: minInst.toString() });
                setAlertModal({
                  open: true,
                  title: language === "ar" ? "الحد الأدنى للأقساط" : "Minimum installments",
                  message:
                    language === "ar"
                      ? `الحد الأدنى للأقساط هو ${minInst} شهر`
                      : `Minimum installments is ${minInst} months`,
                  variant: "info",
                  showCancel: false,
                  onConfirm: () => setAlertModal((prev) => ({ ...prev, open: false })),
                });
              } else {
                setAdvancementForm({ ...advancementForm, numberOfInstallments: e.target.value });
              }
            }}
            placeholder={t.numberOfInstallmentsPlaceholder}
            min={advancementLimits?.installments?.min || 1}
            max={advancementLimits?.installments?.max || 12}
            onFocus={focusStyle}
            onBlur={blurStyle}
          />
        </div>

        {/* Real-time Validation Feedback */}
        {advancementValidation &&
          advancementForm.requestedAmount &&
          advancementForm.numberOfInstallments && (
            <div
              style={{
                background: advancementValidation.isValid
                  ? theme === "dark" ? "rgba(16, 185, 129, 0.15)" : "rgba(16, 185, 129, 0.08)"
                  : theme === "dark" ? "rgba(239, 68, 68, 0.15)" : "rgba(239, 68, 68, 0.08)",
                border: `2px solid ${advancementValidation.isValid ? "#10b981" : "#ef4444"}`,
                borderRadius: "16px",
                padding: "20px",
                marginBottom: "24px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
                {advancementValidation.isValid ? (
                  <CheckCircle size={24} color="#10b981" />
                ) : (
                  <AlertCircle size={24} color="#ef4444" />
                )}
                <span style={{ fontSize: "17px", fontWeight: "800", color: advancementValidation.isValid ? "#10b981" : "#ef4444" }}>
                  {advancementValidation.isValid
                    ? language === "ar" ? "الطلب صالح ✓" : "Request Valid ✓"
                    : language === "ar" ? "يرجى التحقق من البيانات" : "Please Check Requirements"}
                </span>
              </div>

              {advancementValidation.errors?.length > 0 && (
                <div style={{ marginBottom: "12px" }}>
                  {advancementValidation.errors.map((error, idx) => (
                    <div
                      key={idx}
                      style={{
                        fontSize: "14px",
                        color: "#ef4444",
                        marginBottom: "6px",
                        paddingLeft: isRTL ? "0" : "20px",
                        paddingRight: isRTL ? "20px" : "0",
                        position: "relative",
                      }}
                    >
                      <span style={{ position: "absolute", [isRTL ? "right" : "left"]: "0", fontWeight: "800" }}>✗</span>
                      {error}
                    </div>
                  ))}
                </div>
              )}

              {advancementValidation.warnings?.length > 0 && (
                <div>
                  {advancementValidation.warnings.map((warning, idx) => (
                    <div
                      key={idx}
                      style={{
                        fontSize: "14px",
                        color: "#f59e0b",
                        marginBottom: "6px",
                        paddingLeft: isRTL ? "0" : "20px",
                        paddingRight: isRTL ? "20px" : "0",
                        position: "relative",
                      }}
                    >
                      <span style={{ position: "absolute", [isRTL ? "right" : "left"]: "0", fontWeight: "800" }}>⚠</span>
                      {warning}
                    </div>
                  ))}
                </div>
              )}

              {advancementValidation.autoApprove && (
                <div style={{ marginTop: "12px", padding: "12px", background: "rgba(16, 185, 129, 0.15)", borderRadius: "10px", fontSize: "14px", color: "#10b981", fontWeight: "600" }}>
                  {language === "ar" ? "✓ سيتم الموافقة على هذا الطلب تلقائياً" : "✓ This request will be auto-approved"}
                </div>
              )}
            </div>
          )}

        {/* Repayment Plan Summary */}
        {advancementForm.requestedAmount && advancementForm.numberOfInstallments && (
          <div style={styles.repaymentPlanBox}>
            <div style={styles.repaymentPlanHeader}>
              <AlertCircle size={24} color="#3b82f6" />
              <span style={styles.repaymentPlanTitle}>{t.repaymentPlan}</span>
            </div>
            <div style={styles.repaymentPlanText}>
              {advancementForm.numberOfInstallments} {t.monthlyInstallments}{" "}
              <strong>
                {formatCurrency(advancementForm.requestedAmount / advancementForm.numberOfInstallments)}
              </strong>
            </div>
          </div>
        )}

        {/* Skip notification checkbox */}
        <div style={{ display: "flex", alignItems: "center", marginBottom: "16px" }}>
          <input
            type="checkbox"
            id="skipAdvancementNotification"
            checked={advancementForm.skipConfirmationNotification || false}
            onChange={(e) => setAdvancementForm({ ...advancementForm, skipConfirmationNotification: e.target.checked })}
            style={{ marginRight: "8px", width: "16px", height: "16px", cursor: "pointer" }}
          />
          <label htmlFor="skipAdvancementNotification" style={{
            fontSize: "13px",
            fontWeight: "600",
            color: theme === "dark" ? "rgba(255,255,255,0.7)" : "#6b7280",
            cursor: "pointer",
          }}>
            {language === "ar" ? "تخطي إشعار التأكيد" : "Skip confirmation notification"}
          </label>
        </div>

        {/* Actions */}
        <div style={styles.modalActions}>
          <button style={styles.secondaryButton} onClick={handleClose}>
            {t.cancel}
          </button>
          <button style={styles.primaryButton} onClick={handleAdvancementSubmit}>
            <Send size={20} />
            {t.submitRequest}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdvancementModal;